import React from 'react'
import { cn } from '@/utils'
import './Button.css'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The button variant
   * @default 'primary'
   */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost'
  
  /**
   * The button size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg'
  
  /**
   * Whether the button is in a loading state
   * @default false
   */
  loading?: boolean
  
  /**
   * Icon to display before the button text
   */
  leftIcon?: React.ReactNode
  
  /**
   * Icon to display after the button text
   */
  rightIcon?: React.ReactNode
  
  /**
   * Whether the button should take the full width of its container
   * @default false
   */
  fullWidth?: boolean
  
  /**
   * The button content
   */
  children?: React.ReactNode
}

/**
 * A versatile button component with multiple variants, sizes, and states.
 * Supports icons, loading states, and accessibility features.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      leftIcon,
      rightIcon,
      fullWidth = false,
      className,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading

    return (
      <button
        ref={ref}
        className={cn(
          'rc-button',
          `rc-button--${variant}`,
          `rc-button--${size}`,
          {
            'rc-button--loading': loading,
            'rc-button--full-width': fullWidth,
          },
          className
        )}
        disabled={isDisabled}
        {...props}
      >
        {loading && (
          <span className="rc-button__spinner" aria-hidden="true">
            <svg
              className="rc-button__spinner-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                className="rc-button__spinner-circle"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="rc-button__spinner-path"
                fill="currentColor"
                d="m12 2a10 10 0 0 1 10 10h-4a6 6 0 0 0-6-6V2z"
              />
            </svg>
          </span>
        )}
        
        {leftIcon && !loading && (
          <span className="rc-button__icon rc-button__icon--left" aria-hidden="true">
            {leftIcon}
          </span>
        )}
        
        {children && (
          <span className="rc-button__content">
            {children}
          </span>
        )}
        
        {rightIcon && !loading && (
          <span className="rc-button__icon rc-button__icon--right" aria-hidden="true">
            {rightIcon}
          </span>
        )}
      </button>
    )
  }
)

Button.displayName = 'Button'